import { Action, createReducer, on } from '@ngrx/store';

import {
  addMeetingAttachment,
  fetchAgendaDecisionStatusOptions,
  fetchAgendaDecisionStatusOptionsSuccess,
  fetchMeetingContent,
  fetchMeetingContentFailure,
  fetchMeetingContentSuccess,
  fetchMeetingHeader,
  fetchMeetingHeaderFailure,
  fetchMeetingHeaderSuccess,
  fetchMeetingOptionsMenu,
  fetchMeetingOptionsMenuFailure,
  fetchMeetingOptionsMenuSuccess,
  fetchMeetingPrimaryActions,
  fetchMeetingPrimaryActionsFailure,
  fetchMeetingPrimaryActionsSuccess,
  fetchMeetingSidebar,
  fetchMeetingSidebarFailure,
  fetchMeetingSidebarSuccess,
  fetchMoveMeetingItemDates,
  fetchMoveMeetingItemDatesFailure,
  fetchMoveMeetingItemDatesSuccess,
  fetchMoveMeetingItemOrgans,
  fetchMoveMeetingItemOrgansFailure,
  fetchMoveMeetingItemOrgansSuccess,
  removeMeetingAttachmentFailure,
  removeMeetingAttachmentSuccess,
  updateMeetingAdditionalInfo,
  updateMeetingAdditionalInfoFailure,
  updateMeetingHeader,
  updateMeetingHeaderFailure,
  updateMeetingHeaderSuccess
} from './meeting.actions';
import {
  AgendaDecisionStatus,
  MeetingAdditionalInfo,
  Document,
  MeetingPrimaryActions,
  MeetingSidebar,
  MeetingOptionsMenuActions,
  MeetingItem
} from './meeting.models';
import { ClosableDateOption } from './meeting.types';

import { ListOption } from '~/repositories/widgets/store/widgets.models';
import { DetailHeader } from '~/ui/detail/detail-header/detail-header.types';

export interface MeetingState {
  error: any;
  meetingHeader: DetailHeader;
  loadingMeetingHeader: boolean;
  meetingContent: MeetingItem[];
  loadingMeetingContent: boolean;
  meetingSidebar: MeetingSidebar;
  loadingMeetingSidebar: boolean;
  meetingPrimaryActions: MeetingPrimaryActions;
  loadingMeetingPrimaryActions: boolean;
  meetingOptionsMenuActions: MeetingOptionsMenuActions;
  loadingMeetingOptionsMenuActions: boolean;
  meetingAdditionalInfo: MeetingAdditionalInfo;
  loadingMeetingAdditionalInfo: boolean;
  meetingAttachments: Document[];
  loadingMeetingAttachments: boolean;
  agendaDecisionStatusOptions: AgendaDecisionStatus[];
  loadingAgendaDecisionStatusOptions: boolean;
  moveMeetingItemOrgans: ListOption[];
  loadingMoveMeetingItemOrgans: boolean;
  moveMeetingItemDates: ClosableDateOption[];
  loadingMoveMeetingItemDates: boolean;
}

export const initialState: MeetingState = {
  // set initial required properties
  error: null,
  meetingHeader: null,
  loadingMeetingHeader: false,
  meetingContent: null,
  loadingMeetingContent: false,
  meetingSidebar: null,
  loadingMeetingSidebar: false,
  meetingPrimaryActions: null,
  loadingMeetingPrimaryActions: false,
  meetingOptionsMenuActions: null,
  loadingMeetingOptionsMenuActions: false,
  meetingAdditionalInfo: null,
  loadingMeetingAdditionalInfo: false,
  meetingAttachments: null,
  loadingMeetingAttachments: false,
  agendaDecisionStatusOptions: null,
  loadingAgendaDecisionStatusOptions: false,
  moveMeetingItemOrgans: null,
  loadingMoveMeetingItemOrgans: false,
  moveMeetingItemDates: null,
  loadingMoveMeetingItemDates: false
};

const reducer = createReducer(
  initialState,
  on(...[fetchMeetingHeader, updateMeetingHeader], (state) => ({
    ...state,
    meetingHeader: null,
    loadingMeetingHeader: true,
    error: null
  })),
  on(...[fetchMeetingHeaderSuccess, updateMeetingHeaderSuccess], (state, { data }) => ({
    ...state,
    meetingHeader: data,
    loadingMeetingHeader: false,
    error: null
  })),
  on(...[fetchMeetingHeaderFailure, updateMeetingHeaderFailure], (state, { error }) => ({
    ...state,
    meetingHeader: null,
    loadingMeetingHeader: false,
    error
  })),

  on(fetchMeetingContent, (state) => ({
    ...state,
    meetingContent: null,
    loadingMeetingContent: true,
    error: null
  })),
  on(fetchMeetingContentSuccess, (state, { data }) => ({
    ...state,
    meetingContent: data,
    loadingMeetingContent: false,
    error: null
  })),
  on(fetchMeetingContentFailure, (state, { error }) => ({
    ...state,
    meetingContent: null,
    loadingMeetingContent: false,
    error
  })),
  on(fetchMeetingSidebar, (state) => ({
    ...state,
    meetingSidebar: null,
    loadingMeetingSidebar: true,
    error: null
  })),
  on(fetchMeetingSidebarSuccess, (state, { data }) => ({
    ...state,
    meetingSidebar: data,
    loadingMeetingSidebar: false,
    error: null
  })),
  on(fetchMeetingSidebarFailure, (state, { error }) => ({
    ...state,
    meetingSidebar: null,
    loadingMeetingSidebar: false,
    error
  })),
  on(fetchMeetingPrimaryActions, (state) => ({
    ...state,
    meetingSidebar: null,
    loadingMeetingPrimaryActions: true,
    error: null
  })),
  on(fetchMeetingPrimaryActionsSuccess, (state, { data }) => ({
    ...state,
    meetingPrimaryActions: data,
    loadingMeetingPrimaryActions: false,
    error: null
  })),
  on(fetchMeetingPrimaryActionsFailure, (state, { error }) => ({
    ...state,
    meetingPrimaryActions: null,
    loadingMeetingPrimaryActions: false,
    error
  })),
  on(fetchMeetingOptionsMenu, (state) => ({
    ...state,
    meetingOptionsMenuActions: null,
    loadingMeetingOptionsMenuActions: true,
    error: null
  })),
  on(fetchMeetingOptionsMenuSuccess, (state, { data }) => ({
    ...state,
    meetingOptionsMenuActions: data,
    loadingMeetingOptionsMenuActions: false,
    error: null
  })),
  on(fetchMeetingOptionsMenuFailure, (state, { error }) => ({
    ...state,
    meetingOptionsMenuActions: null,
    loadingMeetingOptionsMenuActions: false,
    error
  })),
  on(updateMeetingAdditionalInfo, (state, { data }) => ({
    ...state,
    meetingHeader: {
      ...state.meetingHeader,
      additionalInfo: data.additionalInfoText
    },
    error: null
  })),
  on(updateMeetingAdditionalInfoFailure, (state, { error }) => ({
    ...state,
    error
  })),
  on(addMeetingAttachment, (state, { data }) => ({
    ...state,
    meetingHeader: {
      ...state.meetingHeader,
      additionalInfoAttachments: [data, ...state.meetingHeader.additionalInfoAttachments]
    }
  })),
  on(removeMeetingAttachmentSuccess, (state, { id }) => ({
    ...state,
    meetingHeader: {
      ...state.meetingHeader,
      additionalInfoAttachments: state.meetingHeader.additionalInfoAttachments.filter(
        (attachment) => attachment.documentId !== id
      )
    },
    error: null
  })),
  on(removeMeetingAttachmentFailure, (state, { error }) => ({
    ...state,
    error
  })),
  on(fetchAgendaDecisionStatusOptions, (state) => ({
    ...state,
    error: null
  })),
  on(fetchAgendaDecisionStatusOptionsSuccess, (state, { data }) => ({
    ...state,
    loadingAgendaDecisionStatusOptions: false,
    agendaDecisionStatusOptions: data,
    error: null
  })),
  on(fetchMoveMeetingItemOrgans, (state) => ({
    ...state,
    error: null
  })),
  on(fetchMoveMeetingItemOrgansSuccess, (state, { data }) => ({
    ...state,
    loadingMoveMeetingItemOrgans: false,
    moveMeetingItemOrgans: data,
    error: null
  })),
  on(fetchMoveMeetingItemOrgansFailure, (state, { error }) => ({
    ...state,
    loadingMoveMeetingItemOrgans: false,
    error
  })),
  on(fetchMoveMeetingItemDates, (state) => ({
    ...state,
    error: null
  })),
  on(fetchMoveMeetingItemDatesSuccess, (state, { data }) => ({
    ...state,
    loadingMoveMeetingItemDates: false,
    moveMeetingItemDates: data,
    error: null
  })),
  on(fetchMoveMeetingItemDatesFailure, (state, { error }) => ({
    ...state,
    loadingMoveMeetingItemDates: false,
    error
  }))
);

export function meetingReducer(state: MeetingState | undefined, action: Action) {
  return reducer(state, action);
}
